/* 
 * MiniAppsTranslation 
 * 
 * Ce composant permet la traduction des textes des mini-apps, y compris Order Tracking.
 * - Order Tracking est considérée comme une mini-app, mais ses textes sont stockés dans une structure différente 
 * - Nous l'intégrons dans l'interface de traduction des mini-apps pour une meilleure expérience utilisateur
 * - Les textes traduits sont synchronisés avec la section orderTracking du store de traduction
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ShoppingBag } from 'lucide-react';
import { useMiniApps } from '../../../../hooks/useMiniApps';
import { useProductSystem } from '../../../../hooks/useProductSystem';
import { useRealEstateSystem } from '../../../../hooks/useRealEstateSystem';
import { useChatbot } from '../../../../contexts/ChatbotContext';
import { supabase } from '../../../../lib/supabase';
import { MiniAppTranslationContent } from './MiniAppTranslationContent';

interface Service {
  id: string;
  title: string;
  subtitle: string | null;
  content: string;
  button_text?: string | null;
  button_url?: string | null;
}

interface MiniAppsTranslationProps {
  language: {
    label: string;
  };
  formData: Array<{
    id: string;
    label: string;
    content?: {
      title?: string;
      description?: string;
      buttonText?: string;
      catalogTitle?: string;
      catalogSubtitle?: string;
      searchPlaceholder?: string;
      filterLabel?: string;
      sortLabel?: string;
      priceLabel?: string;
      stockLabel?: string;
      categoryLabel?: string;
      tagsLabel?: string;
      surfaceLabel?: string;
      roomsLabel?: string;
      locationLabel?: string;
      featuresLabel?: string;
      availableLabel?: string;
      soldLabel?: string;
      rentedLabel?: string;
      contactButtonLabel?: string;
      productDetails?: {
        [id: string]: {
          name: string;
          description: string;
        };
      };
      serviceDetails?: {
        [id: string]: {
          title: string;
          subtitle: string;
          content: string;
          buttonText: string;
        };
      };
      propertyDetails?: {
        [id: string]: {
          title: string;
          description: string;
          features: string[];
        };
      };
      // Champs spécifiques pour Order Tracking
      quickActionText?: string;
      promptMessage?: string;
      errorMessage?: string;
    };
  }>;
  onChange: (data: any) => void;
  orderTrackingData?: {
    quickActionText: string;
    promptMessage: string;
    errorMessage: string;
  };
}

export function MiniAppsTranslation({ 
  language, 
  formData, 
  onChange,
  orderTrackingData 
}: MiniAppsTranslationProps) {
  const { miniApps, initialized: miniAppsInitialized, initializeMiniApps } = useMiniApps();
  const { products, initialized: productsInitialized, initialize: initializeProducts } = useProductSystem();
  const { properties, initialized: propertiesInitialized, initialize: initializeProperties } = useRealEstateSystem();
  const { chatbotPublicId } = useChatbot();
  const [selectedMiniApp, setSelectedMiniApp] = useState<string | null>(null);
  const [showMiniAppsList, setShowMiniAppsList] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  // Initialize all required data
  useEffect(() => {
    const initializeData = async () => {
      if (!chatbotPublicId) return;

      setLoading(true);
      try {
        // Initialize mini apps if not already initialized
        if (!miniAppsInitialized) {
          await initializeMiniApps(chatbotPublicId);
        }

        // Initialize products if not already initialized
        if (!productsInitialized) {
          await initializeProducts(chatbotPublicId);
        }

        // Initialize properties if not already initialized
        if (!propertiesInitialized) {
          await initializeProperties(chatbotPublicId);
        }

        // Fetch services
        const { data: servicesData } = await supabase
          .from('services')
          .select('*')
          .eq('chatbot_public_id', chatbotPublicId)
          .order('position');

        if (servicesData) {
          setServices(servicesData);
        }
      } catch (err) {
        console.error('Error initializing translation data:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [
    chatbotPublicId,
    miniAppsInitialized,
    productsInitialized,
    propertiesInitialized,
    initializeMiniApps,
    initializeProducts,
    initializeProperties
  ]);

  // Get all enabled mini-apps
  let enabledMiniApps = miniApps.filter(app => app.enabled);
  
  // Si l'app order-tracking n'existe pas, l'ajouter manuellement
  const hasOrderTracking = enabledMiniApps.some(app => app.id === 'order-tracking');
  if (!hasOrderTracking) {
    // Définir le label en fonction de la langue
    const orderTrackingLabel = language.label === 'Français' ? 'Suivi de Commande' : 'Order Tracking';
    
    enabledMiniApps = [
      ...enabledMiniApps,
      {
        id: 'order-tracking',
        label: orderTrackingLabel,
        title: orderTrackingLabel,
        enabled: true,
        buttonVisible: true
      }
    ];
  }

  const updateMiniAppData = (appId: string, updates: any) => {
    const newMiniApps = [...formData];
    const appIndex = newMiniApps.findIndex(a => a.id === appId);
    
    if (appIndex >= 0) {
      newMiniApps[appIndex] = {
        ...newMiniApps[appIndex],
        ...updates
      };
    } else {
      newMiniApps.push({
        id: appId,
        ...updates
      });
    }
    
    onChange(newMiniApps);
  };
  
  // Fonction spéciale pour Order Tracking
  const updateOrderTrackingField = (field: string, value: string) => {
    // Trouver la mini-app order-tracking ou la créer
    const newMiniApps = [...formData];
    const orderTrackingIndex = newMiniApps.findIndex(a => a.id === 'order-tracking');
    
    if (orderTrackingIndex >= 0) {
      // Mise à jour du champ existant
      newMiniApps[orderTrackingIndex] = {
        ...newMiniApps[orderTrackingIndex],
        content: {
          ...newMiniApps[orderTrackingIndex].content,
          [field]: value
        }
      };
    } else {
      // Créer une nouvelle entrée
      newMiniApps.push({
        id: 'order-tracking',
        label: 'Order Tracking',
        content: {
          [field]: value
        }
      });
    }
    
    onChange(newMiniApps);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  // Trouver les données de Order Tracking dans les mini-apps
  const orderTrackingTranslation = formData.find(app => app.id === 'order-tracking');

  return (
    <div className="space-y-6 w-full">
      {/* Mini App Selector - Avec style amélioré pour plus de largeur et une meilleure visibilité */}
      <div className="relative w-full">
        <button
          type="button"
          onClick={() => setShowMiniAppsList(!showMiniAppsList)}
          className="w-full flex items-center justify-between px-5 py-4 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition-colors"
        >
          <span className="text-base font-medium">
            {selectedMiniApp 
              ? enabledMiniApps.find(app => app.id === selectedMiniApp)?.label 
              : 'Select a mini app'}
          </span>
          {showMiniAppsList ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>

        {showMiniAppsList && (
          <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-[350px] overflow-y-auto">
            {enabledMiniApps.map(app => (
              <button
                key={app.id}
                type="button"
                onClick={() => {
                  setSelectedMiniApp(app.id);
                  setShowMiniAppsList(false);
                }}
                className="w-full px-5 py-3.5 text-left text-white hover:bg-white/5 transition-colors flex items-center gap-3"
              >
                {app.id === 'order-tracking' && (
                  <ShoppingBag className="w-5 h-5 text-indigo-400" />
                )}
                <span className="block font-medium">{app.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Mini App Content */}
      {selectedMiniApp && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10 w-full">
          {/* Rendu spécial pour Order Tracking */}
          {selectedMiniApp === 'order-tracking' ? (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-white mb-4">Order Tracking Translations</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Quick Action Text (original)
                </label>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-400">
                  Track my order
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Quick Action Text translated
                </label>
                <input
                  type="text"
                  value={orderTrackingTranslation?.content?.quickActionText || ''}
                  onChange={(e) => updateOrderTrackingField('quickActionText', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={`Quick action text in ${language.label}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Prompt Message (original)
                </label>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-400">
                  Please enter your order number to track your order:
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Prompt Message translated
                </label>
                <input
                  type="text"
                  value={orderTrackingTranslation?.content?.promptMessage || ''}
                  onChange={(e) => updateOrderTrackingField('promptMessage', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={`Prompt message in ${language.label}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Error Message (original)
                </label>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-400">
                  Sorry, we couldn't find that order. Please check the order number and try again.
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Error Message translated
                </label>
                <textarea
                  value={orderTrackingTranslation?.content?.errorMessage || ''}
                  onChange={(e) => updateOrderTrackingField('errorMessage', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={`Error message in ${language.label}`}
                  rows={3}
                />
              </div>
            </div>
          ) : (
            // Rendu normal pour les autres mini-apps
            enabledMiniApps.map((app) => {
              if (app.id !== selectedMiniApp) return null;

              const miniAppTranslation = formData.find(m => m.id === app.id) || {
                id: app.id,
                label: '',
                content: {}
              };

              return (
                <MiniAppTranslationContent
                  key={app.id}
                  app={app}
                  miniAppTranslation={miniAppTranslation}
                  language={language}
                  products={products}
                  services={services}
                  properties={properties}
                  onUpdate={(updates) => updateMiniAppData(app.id, updates)}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
}